import {log, callAdminAjax, updateLoadingSpinner, getTemplate, hasKey, getValue, textToParagraphs, normalizeString, nextFrame, sanitizeWpTitle, createTranslator, getTranslatedString} from './utils.js';
import { fetchAPIData, getFetchedAPIData, getAllPlaceData, getReversedPlaceData } from './mapPlannerShared.js';
import { initUserMediaUploads } from './user-media-uploads.js';
import { createFilterEngine } from './filterEngine.js';
import { destroySwipersIn, initSwipersIn, getSinglePlace_servicesSwiper_swiperInstance } from './hulk.js';
import { filterMasterServicesList, getRoutePlaces, renderFilteredPlaceList, addChildRoutePlaces, disableRoutePlacesFromOrigin, sliceRoutePlacesToDestination, addCalculatedValuesFromOrigin, setGlobalPathStops } from './placeList.js';
import { showActionMenuModal, openSimpleModal } from './common.js';
import { setFenixBackdrop, setFenixModalContent, openFenixModal, setFenixModalPurpose, closeFenixModal } from './fenixModal.js';

const $ = window.jQuery;

// Create a translator function for the directory page.
const t = createTranslator('directory');

// Global variable to store the filter engine instance.
let placePageFilterEngine = null;

/**
 * Initialize the directory page.
 */
export function initDirectoryPage() {
    log('Directory page initialized');

    initDirectoryPageUI();

    // Allow for user media uploads on directory pages. Logged in users only.
    // Not needed for directory home page.
    if(window.ironman.userData.loggedInUser && window.ironman.waldo !== 'directory-home') {
        initUserMediaUploads();
    }

    if(window.ironman.waldo === 'place') {
        initPlacePage();
    } else if(window.ironman.waldo === 'listing') {
        initListingPage();
    } else if(window.ironman.waldo === 'camino') {
        initCaminoPage();
    } else if(window.ironman.waldo === 'directory-home') {
        initDirectoryHomePage();
    }
}

function initPlacePage() {
    log('Place page initialized');

    const placeId = parseInt(window.ironman.placeId);
    if(!placeId) {
        log('No place id found');
        return false;
    }

    getPlaceListings(placeId, Object.keys(window.ironman.appConfig.servicesOffered), 'service_types');
}

/**
 * Get and render the filter engine for the place listings/hotels for a given place id.
 * Shared by both the single place page and the directory search page.
 * @param {number} placeId - The id of the place.
 * @param {array} serviceAllowedValues - The allowed values for the service types filter.
 * @param {string} serviceTypeFieldQuery - The API field to get a listing's matching service values from. E.g. 'service_types' or 'services'. If we're searching for a category, use service_types, if we're searching for child services, use services.
 * @returns {void}
 */
function getPlaceListings(placeId, serviceAllowedValues=[], serviceTypeFieldQuery='service_types') {
    log('Getting place listings for place id:', placeId);

    // Initialize the place page filter engine with two groups: hotel and listing.
    placePageFilterEngine = createFilterEngine({
        historyMode: 'push', // 'push' or 'replace' depending on whether you want to allow the back button to step through filter changes
        groups: [
            {
                id: 'hotel', // the id of the group, used to identify the group in the setGroupFilters function
                dataKey: 'hotel', // the key of the data in the dataByKey object, used to identify the data in the setData function
                // cardContainer: '#hotel-cards', // the container for the cards, used to render the cards
                filters: [
                    {
                        name: 'types', // the name of the filter, used to identify the filter in the setGroupFilters function
                        param: 'types', // combined with the group id to form the URL param key, e.g. hotel_types
                        // selector: '[data-hotel-filter-type]', // the selector for the filter, used to select the filter in the DOM
                        allowedValues: Object.keys(window.ironman.appConfig.servicesOffered.sleep.children), //['albergue', 'hotel'], // the allowed values for the filter, used to set the allowed values in the DOM
                        getItemValues: item => item.services || [], // the function to get the values for the filter, used to get the values for the filter in the DOM
                    },
                    {
                        name: 'amenities', // the name of the filter, used to identify the filter in the setGroupFilters function
                        param: 'amenities', // combined with the group id to form the URL param key, e.g. hotel_amenities
                        // selector: '[data-hotel-filter-amenity]', // the selector for the filter, used to select the filter in the DOM
                        allowedValues: Object.keys(window.ironman.appConfig.hotelAmenities), //['albergue', 'hotel'], // the allowed values for the filter, used to set the allowed values in the DOM
                        getItemValues: item => item.hotel_amenities || [], // the function to get the values for the filter, used to get the values for the filter in the DOM
                        matchMode: 'all', // require ALL selected amenities
                    },
                    {
                        name: 'roomTypes', // the name of the filter, used to identify the filter in the setGroupFilters function
                        param: 'roomTypes', // combined with the group id to form the URL param key, e.g. hotel_roomTypes
                        // selector: '[data-hotel-filter-room-type]', // the selector for the filter, used to select the filter in the DOM
                        allowedValues: Object.keys(window.ironman.appConfig.hotelRoomTypes), //['albergue', 'hotel'], // the allowed values for the filter, used to set the allowed values in the DOM
                        getItemValues: item => item.hotel_room_types || [], // the function to get the values for the filter, used to get the values for the filter in the DOM
                        // matchMode: 'all', // require ALL selected room types
                    },
                ],
                sort: {
                    param: 'sort',
                    selector: '#hotel-sort-select', // the selector for the sort, used to select the sort in the DOM
                    allowedKeys: ['title-asc', 'rating_average-desc', 'rating_count-desc'],
                    defaultKey: 'rating_average-desc', // the default key for the sort, used to set the default sort in the DOM
                    keyToSpec: key => {
                        const [field, dir] = (key || 'title-asc').split('-');
                        return { field: field || 'title', dir: dir || 'asc' }; // the field and direction for the sort, used to set the sort in the DOM
                    },
                },
                pagination: {
                    pageParam: 'page', // the parameter for the page, used to form the URL param key, e.g. hotels_page
                    pageSize: 20, // the size of the page, used to set the page size in the DOM
                    defaultPage: 1, // the default page, used to set the default page in the DOM
                },
                render: hotelRender, // the function to render the cards, used to render the cards in the DOM
                onStateChange(state, group) {
                    const activeTypes = state.filters.types || [];
                    const activeAmenities = state.filters.amenities || [];
                    const activeRoomTypes = state.filters.roomTypes || [];
                    log('Active types', activeTypes);
                    $('input[name="hotel-type-filters"]').each(function () {
                        this.checked = activeTypes.includes(this.value);
                    });
                    log('Active amenities', activeAmenities);
                    $('input[name="amenities-filters"]').each(function () {
                        this.checked = activeAmenities.includes(this.value);
                    });
                    log('Active room types', activeRoomTypes);
                    $('input[name="room-type-filters"]').each(function () {
                        this.checked = activeRoomTypes.includes(this.value);
                    });
                },
            },
            {
                id: 'listing', // the id of the group, used to identify the group in the setGroupFilters function
                dataKey: 'listing', // the key of the data in the dataByKey object, used to identify the data in the setData function
                filters: [
                    {
                        name: 'types', // the name of the filter, used to identify the filter in the setGroupFilters function
                        param: 'types', // combined with the group id to form the URL param key, e.g. listing_types
                        // selector: '[data-listing-filter-type]', // the selector for the filter, used to select the filter in the DOM
                        allowedValues: serviceAllowedValues, // the allowed values for the filter, used to set the allowed values in the DOM
                        getItemValues: item => item[serviceTypeFieldQuery] || [], // the function to get the values for the filter, used to get the values for the filter in the DOM
                    }
                ],
                sort: {
                    param: 'sort',
                    selector: '#listing-sort-select', // the selector for the sort, used to select the sort in the DOM
                    allowedKeys: ['title-asc', 'rating_average-desc', 'rating_count-desc'],
                    defaultKey: 'title-asc', // the default key for the sort, used to set the default sort in the DOM
                    keyToSpec: key => {
                        const [field, dir] = (key || 'title-asc').split('-');
                        return { field: field || 'title', dir: dir || 'asc' }; // the field and direction for the sort, used to set the sort in the DOM
                    },
                },
                pagination: {
                    pageParam: 'page', // the parameter for the page, used to form the URL param key, e.g. listings_page
                    pageSize: 20, // the size of the page, used to set the page size in the DOM
                    defaultPage: 1, // the default page, used to set the default page in the DOM
                },
                render: listingRender, // the function to render the cards, used to render the cards in the DOM
                // hook for custom UI if not using selector mentioned above.
                onStateChange(state, group) {
                    const activeTypes = state.filters.types || [];
                    log('Active types', activeTypes);
                    $('input[name="service-filters"]').each(function () {
                        this.checked = activeTypes.includes(this.value);
                    });
                
                    $('[data-label="resetServiceFiltersButton"]').toggleClass('d-none', activeTypes.length === 0);
                    $('[data-label="showAllServicesButton"]').toggleClass('d-none', activeTypes.length === 0);
                },
            }
        ]
    });
    placePageFilterEngine.init();
    window.placePageFilterEngine = placePageFilterEngine;
    // To update filters, call: placePageFilterEngine.setGroupFilters('hotel', { filters: { services: ['hotel'] } });
    
    // Fetch Listing API data upon pageload. Either from cache or by downloading it.
    fetchAPIData('listings', null, placeId, 'non-blocking', t('downloadingListingsDataSpinnerMessage')).then(function() {
        getFetchedAPIData('listings', null, placeId).then(function(listingsAPIData) {
            // Listings API data is now ready to use.
            log('Listings API data loaded successfully.', listingsAPIData);

            const hotelServices = Object.keys(window.ironman.appConfig.servicesOffered.sleep.children);
            const listingServices = [];
            $.each(window.ironman.appConfig.servicesOffered, function(slug, service) {
                if(slug === 'sleep') {
                    return; // Skip the sleep category.
                }
                $.each(service.children, function(childSlug, child) {
                    listingServices.push(childSlug);
                });
            });
            log('Hotel services', hotelServices);
            log('Listing services', listingServices);

            // Separate the listings data into hotels and listings and set the data for the filter engines.
            const allListings = Object.values(listingsAPIData.listings);
            const hotelsData = allListings.filter(listing => hotelServices.some(type => listing.services.includes(type)));
            const listingsData = allListings.filter(listing => listingServices.some(type => listing.services.includes(type)));

            // Directory Search Page only.
            if(window.ironman.getPlaceListingsCat) {
                // Count matching listings in this category.
                let count = 0;

                // Set the data for either the the hotel filter engine or the listing filter engine.
                if(window.ironman.getPlaceListingsCat === 'sleep') {
                    placePageFilterEngine.setData('hotel', hotelsData);
                    count = hotelsData.length;
                } else {
                    // Filter out irrelevant listings.
                    const filteredListingsData = listingsData.filter(listing => listing.service_types.includes(window.ironman.getPlaceListingsCat));
                    placePageFilterEngine.setData('listing', filteredListingsData);
                    count = filteredListingsData.length;
                }

                // Update the count in the UI.
                $('[data-label="searchResultsCount"]').text(count);
                $('[data-label="searchResultsCountText"]').removeClass('d-none');
            } else {
                // Place page (doesn't have window.ironman.getPlaceListingsCat set)
                // Set the data for BOTH filter engines.
                placePageFilterEngine.setData('hotel', hotelsData);
                placePageFilterEngine.setData('listing', listingsData);

                // log('Listings data', listingsData);
                // log('Hotels data', hotelsData);
            }

            // Mark the data as loaded and perform the first render.
            placePageFilterEngine.finishLoading();
        });
    });
}

function initListingPage() {
    log('Listing page initialized');
}

function initCaminoPage() {
    log('Camino page initialized');

    // Update the ways stoplist upon pageload.
    updateWaysStoplist();
}

function initDirectoryHomePage() {
    log('Directory home page initialized');

    // Search button
    $(document).on('click', '[data-action="directory-search-submit"]', function(e) {
        e.preventDefault();
        log('Directory search submitted');
        const $button = $(this);
        const where = $('#directory-where').val();
        const what = $('#directory-what').val();
        log('Where', where);
        log('What', what);
        if(!where || !what) {
            log('No where or what selected');
            return false;
        }
        // Redirect to the directory page with the where and what parameters.
        window.location.href = window.ironman.appConfig.baseUrl+'/directory/?where='+where+'&what='+what;
        // Change button text to animated spinner
        $button.html('<i class="ci-gear animate-spin"></i>');
        return false;
    });

    // Get place listings
    if(hasKey(window.ironman, 'getPlaceListings') && parseInt(window.ironman.getPlaceListings) > 0 && hasKey(window.ironman.appConfig.servicesOffered, window.ironman.getPlaceListingsCat)) {
        getPlaceListings(parseInt(window.ironman.getPlaceListings), Object.keys(window.ironman.appConfig.servicesOffered[window.ironman.getPlaceListingsCat].children), 'services');
    } else {
        // initSwipersIn(); // Initialize the Swiper instance.
    }
}

function initDirectoryPageUI() {
    log('Directory page UI initialized');

    // Suggest Changes button
    $(document).on('click', '[data-action="suggestEdits"]', function(e) {
        e.preventDefault();
        suggestChanges(false);
        return false;
    });
    $(document).on('click', '[data-action="suggestEditsHotel"]', function(e) {
        e.preventDefault();
        suggestChanges(true);
        return false;
    });
    // Report Duplicate button
    $(document).on('click', '[data-action="reportDuplicate"]', function(e) {
        e.preventDefault();
        reportDuplicate();
        return false;
    });
    // Claim Listing button
    $(document).on('click', '[data-action="claimListing"]', function(e) {
        e.preventDefault();
        const listingId = window.ironman.postId;

        // Show Claim Listing modal.
        showClaimListingModal(listingId);
        return false;
    });

    // Claim listing modal submit button
    $(document).on('click', '[data-action="claimListingModal_submit"]', function(e) {
        e.preventDefault();
        log('Claim listing modal submitted');
        const parentPostId = $(this).attr('data-parent-post-id');
        const role = $('#claim-listing-role').val();
        const confirm = $('#claim-listing-confirm').is(':checked');
        if(!role || !confirm || role === 'none') {
            $('[data-label="claim-listing-error-message"]').removeClass('d-none');
            return false;
        } else {
            $('[data-label="claim-listing-error-message"]').addClass('d-none');
        }

        // Create new claim listing post.
        createNewListingClaim();
        return false;
    });

    $(document).on('click', '[data-action="translateContent"]', function(e) {
        e.preventDefault();
        const $button = $(this);
        const target_language = $button.attr('data-lang');

        // Validate target language
        if(!hasKey(window.ironman.appConfig.postContentTranslationLanguages, target_language)) {
            log('Invalid target language:', target_language);
            return false;
        }

        // Confirm there is content to translate.
        if(hasKey(window.ironman, 'postContentTranslations') && hasKey(window.ironman.postContentTranslations, 'original') && window.ironman.postContentTranslations.original.length > 0) {
            // Translate the content.
            handleTranslation(target_language);
        } else {
            log('No content to translate.');
        }        
        return false;
    });

    $(document).on('click', '[data-action="open-improve-listing-modal"]', function(e) {
        log('Opening improve listing modal');
        e.preventDefault();
        
        let menuItems = [];
        if(window.ironman.is_hotel) {
            menuItems = [
                {
                    icon: 'ci-pencil-simple-line',
                    text: t('editListingDetails'),
                    action: 'suggestEdits'
                },
                {
                    icon: 'ci-pencil-simple-line',
                    text: t('editHotelDetails'),
                    action: 'suggestEditsHotel'
                },
            ];
        } else {
            menuItems = [
                {
                    icon: 'ci-pencil-simple-line',
                    text: t('editListing'),
                    action: 'suggestEdits'
                },
            ];
        }

        menuItems.push({
            icon: 'ci-check-circle',
            text: t('claimListing'),
            action: 'createNewListingClaim'
        });

        menuItems.push({
            icon: 'ci-copy',
            text: t('reportDuplicate'),
            action: 'reportDuplicate'
        });

        menuItems.push({
            icon: 'ci-x-circle',
            text: t('reportMistake'),
            action: 'reportListingMistake'
        });
        
        showActionMenuModal(t('improveThisListingModal_title'), menuItems);
    });

    $(document).on('click', '[data-action="open-improve-place-modal"]', function(e) {
        log('Opening improve place modal');
        e.preventDefault();
        
        let menuItems = [
            {
                icon: 'ci-pencil-simple-line',
                text: t('editPlace'),
                action: 'suggestEdits'
            },
            {
                icon: 'ci-x-circle',
                text: t('reportMistake'),
                action: 'reportPlaceMistake'
            }
        ];        
        showActionMenuModal(t('improveThisPlaceModal_title'), menuItems);
    });

    $(document).on('click', '[data-action="open-improve-camino-modal"]', function(e) {
        log('Opening improve camino modal');
        e.preventDefault();
        
        let menuItems = [
            {
                icon: 'ci-pencil-simple-line',
                text: t('editCamino'),
                action: 'suggestEdits'
            },
            {
                icon: 'ci-x-circle',
                text: t('reportMistake'),
                action: 'reportCaminoMistake'
            }
        ];        
        showActionMenuModal(t('improveThisCaminoModal_title'), menuItems);
    });

    $(document).on('click', '[data-action="open-report-listing-modal"]', function(e) {
        log('Opening report listing modal');
        e.preventDefault();
        showActionMenuModal(t('reportThisListingModal_title'), [
            {
                icon: 'ci-copy',
                text: t('reportDuplicate'),
                action: 'reportDuplicate'
            },
            {
                icon: 'ci-x-circle',
                text: t('reportMistake'),
                action: 'reportListingMistake'
            },
        ]);
    });

    $(document).on('click', '[data-action="open-edit-listing-modal"]', function(e) {
        log('Opening edit listing modal');
        e.preventDefault();
        showActionMenuModal(t('editThisListingModal_title'), [
            {
                icon: 'ci-pencil-simple-line',
                text: t('editListing'),
                link: window.ironman.appConfig.editPostFormPage+'?listing_id='+window.ironman.postId
            },
            {
                icon: 'ci-x-circle',
                text: t('editHotelDetails'),
                link: window.ironman.appConfig.editPostFormPage+'?listing_id='+window.ironman.postId+'&hotel_details=true'
            },
        ]);
    });

    $(document).on('click', '[data-action="openNewPostPopup"]', function(e) {
        log('Opening new post popup');
        e.preventDefault();
        const $button = $(this);
        const postType = $button.attr('data-post-type');
        const parentPostId = $button.attr('data-parent-post-id');
        const inputPlaceholder = $button.attr('data-input-placeholder');
        showNewPostModal(postType, parentPostId, inputPlaceholder);
        return false;
    });

    $(document).on('click', '[data-action="newPostModal_submit"]', function(e) {
        log('New post modal submitted');
        e.preventDefault();
        $('[data-label="new-post-error-message"]').addClass('d-none'); // Hide the error message.
        
        const $button = $(this);
        const postType = $button.attr('data-post-type');
        const postTitle = $('#createNewPostModalContainer').find('[data-label="name-input-field"]').val();
        const parentPostId = $button.attr('data-parent-post-id');
        const create = createNewPost(postType, postTitle, parentPostId);
        if(create == false) {
            $('[data-label="new-post-error-message"]').removeClass('d-none');
            return false;
        }
        // CreateNewPost will redirect to the edit form of the new post. No need to close modal.

        // Disable the button though.
        $button.prop('disabled', true).addClass('disabled');
        return false;
    });

    $(document).on('click', '[data-action="openRoomDetailsPopup"]', function(e) {
        log('Opening room details popup');
        e.preventDefault();
        const $button = $(this);
        const roomId = $button.attr('data-room-id');
        openRoomDetailsPopup(roomId);
        return false;
    });

    // Enable navbar scrollspy
    $(window).on('load', function () {
        log('Loading navbar scrollspy');
        // bootstrap.ScrollSpy.getInstance(document.body)?.dispose();
        // bootstrap.ScrollSpy.getInstance(document.documentElement)?.dispose();
        const spy = new bootstrap.ScrollSpy(document.documentElement, {
            target: '#page-nav',
            rootMargin: '-10% 0px -50%'
        });
        // spy.refresh();
    });

    $(document).on('click', '[data-action="openWaysCollapse"]', function(e) {
        // Check if it's opening or closing the collapse.
        const $button = $('#ways-collapse-toggle');
        if(!$button.length) {
            log('Ways collapse toggle button not found');
            return false;
        }
        const isOpening = $button.hasClass('collapsed') === false;
        if(isOpening) {
            log('Opening ways collapse');
            updateWaysStoplist();
        } else {
            log('Closing ways collapse');
        }
        return false;
    });

    $(document).on('click', '[data-action="stoplistUpdate"]', function(e) {
        log('Updating ways stoplist');
        updateWaysStoplist();
        return false;
    });

    $(document).on('click', '[data-action="openWriteReviewModal"]', function(e) {
        log('Opening write review form');
        e.preventDefault();
        openWriteReviewModal();
        return false;
    });

    // $(document).on('click', '[data-action="writeReviewModal_submit"]', function(e) {
    //     log('Write review modal submitted');
    //     e.preventDefault();
    //     $("#fenixBody").find('form.glsr-review-form button[type="submit"]').trigger('click'); // Submit the review form.
    //     return false;
    // });

    $(document).on('click', '[data-action="refreshPage"]', function(e) {
        log('Refreshing page');
        e.preventDefault();
        window.location.reload();
        return false;
    });

    GLSR.Event.on('site-reviews/form/handle', function (response, formEl) {
        console.log('Site Reviews response:', response);
    
        // Adjust this check to the response structure in your installed version.
        if (response?.success === true) {
            $('#review-submitted-alert').removeClass('d-none');
            closeFenixModal();
        }
    });

    // Initialize the listings filtering UI.
    initListingsFilteringUI();

    // Delay 5s for testing
    // setTimeout(function() {
        // Now that all click handlers are set, UN-DISABLE all the buttons with a data-action attribute.
        $('a.btn[data-action]').prop('disabled', false).removeClass('disabled');
        $('button[data-action]').prop('disabled', false).removeClass('disabled');
        
        log('UI Ready');
    // }, 5000);
}

function initListingsFilteringUI() {
    log('Initializing listings filtering UI');

    // Service filters

    // When clicking a link from the top of the page, update the service filters inputs to trigger an update
    $(document).on('click', '[data-action="filter-services"]', function() {
        const $button = $(this);
        const serviceSlug = $button.attr('data-service-slug');
        log('filtering services', serviceSlug);
        $('input[name="service-filters"][value="'+serviceSlug+'"]').prop('checked', true).trigger('change');
        return true; // Continue to scroll to the service section.
    });

    $(document).on('change', 'input[name="service-filters"]', function() {
        const selected = $('input[name="service-filters"]:checked')
            .map(function() {
                return this.value;
            }).get() ?? [];
        log('selected services', selected);
        placePageFilterEngine.setGroupFilters('listing', { filters: { types: selected } });
        $('[data-label="resetServiceFiltersButton"]').removeClass('d-none');
        // Update "Show all services" button
        $('[data-label="showAllServicesButton"]').attr('href', '/directory/?where='+window.ironman.placeSlug+'&what='+selected.join(','));
    });

    // Reset service filters
    $(document).on('click', '[data-action="reset-service-filters"]', function() {
        log('Resetting service filters');
        placePageFilterEngine.setGroupFilters('listing', { filters: { types: [] } });
        // $('input[name="service-filters"]').prop('checked', false);
        // $('[data-label="resetServiceFiltersButton"]').addClass('d-none');
        return false;
    });

    // Hotel type filters
    $(document).on('change', 'input[name="hotel-type-filters"]', function() {
        const selected = $('input[name="hotel-type-filters"]:checked')
            .map(function() {
                return this.value;
            }).get() ?? [];
        log('selected hotel types', selected);
        placePageFilterEngine.setGroupFilters('hotel', { filters: { types: selected } });
    });

    // Reset hotel type filters
    $(document).on('click', '[data-action="reset-hotel-type-filters"]', function() {
        log('Resetting hotel type filters');
        placePageFilterEngine.setGroupFilters('hotel', { filters: { types: [] } });
        return false;
    });

    // Room type filters
    $(document).on('change', 'input[name="room-type-filters"]', function() {
        const selected = $('input[name="room-type-filters"]:checked')
            .map(function() {
                return this.value;
            }).get() ?? [];
        log('selected room types', selected);
        placePageFilterEngine.setGroupFilters('hotel', { filters: { roomTypes: selected } });
    });

    // Reset room type filters
    $(document).on('click', '[data-action="reset-room-type-filters"]', function() {
        log('Resetting room type filters');
        placePageFilterEngine.setGroupFilters('hotel', { filters: { roomTypes: [] } });
        return false;
    });

    // Amenities filters
    $(document).on('change', 'input[name="amenities-filters"]', function() {
        const selected = $('input[name="amenities-filters"]:checked')
            .map(function() {
                return this.value;
            }).get() ?? [];
        log('selected amenities', selected);
        placePageFilterEngine.setGroupFilters('hotel', { filters: { amenities: selected } });
    });

    // Reset amenities filters
    $(document).on('click', '[data-action="reset-amenities-filters"]', function() {
        log('Resetting amenities filters');
        placePageFilterEngine.setGroupFilters('hotel', { filters: { amenities: [] } });
        return false;
    });
}

function updateWaysStoplist() {
    log('Loading ways stoplist');
    
    // POPULATE THE PLACES STOPLIST.
    // --------------------------------------------------------------------------
    fetchAPIData('places', 'all', null, 'non-blocking', t('downloadingPlacesDataSpinnerMessage')).then(function() {
        return getFetchedAPIData('places').then(function() {
            // placeData now ready to use.
            log('Place data loaded successfully.');

            // Get the place list container.
            const $placeList = $('#ways-collapse-content').find('ul.stage-stops-container');
            if(!$placeList.length) {
                log('Place list container not found');
                return false;
            }

            // Get latest filter values or use defaults (e.g. for Single Camino page).
            const routeId = parseInt($('#stoplist_RouteFilter button.nav-link.active').attr('data-value') ?? window.ironman.routeId ?? false);
            if(!routeId) {
                log('Route ID not found');
                return false;
            }
            const direction = $('#stoplist_DirectionFilter button.active').attr('data-value') === 'reverse' ? 'reverse' : 'forward';
            const originId = window.ironman.placeId ?? null;

            // Build the route places list every time the list is opened.
            const allRoutePlaces = getRoutePlaces(routeId, direction, originId);

            let listType = 'stoplist';

            // Add child route places to the route places.
            let filteredRoutePlaces = addChildRoutePlaces(allRoutePlaces, direction);

            // Slice the route places from the origin.
            // if(originId) {
                // filteredRoutePlaces = disableRoutePlacesFromOrigin(filteredRoutePlaces, originId);

                // if(destinationId) {
                //     filteredRoutePlaces = sliceRoutePlacesToDestination(filteredRoutePlaces, destinationId);
                // }

                // Add calculated values from the origin (Distance, Ascending, Descending)
                // filteredRoutePlaces = addCalculatedValuesFromOrigin(filteredRoutePlaces, routeId, originId);
            // }

            // Save to global var (for future reference, e.g. search filtering, recalculating distances on toggle change..)
            // setGlobalPathStops('placePageStoplist', filteredRoutePlaces);

            log('filteredRoutePlaces', filteredRoutePlaces, listType);
            const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, originId, null, null, null, routeId, false, true, true, false, true);
            
            // Write list to the ul container.
            $placeList.empty().append(listFragment);

            // Scroll to selected place if set
            if(originId) {
                const selectedPlaces = document.getElementsByClassName('scroll-to-preselected-place');
                if(selectedPlaces.length > 0) {
                    const child = selectedPlaces[0];
                    const container = $placeList.get(0);
                    const offset = 10;
                    
                    const childRect = child.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    
                    // Force-scroll the li near the top of the ul.
                    // This scrolls only the ul, not the whole page.
                    container.scrollTo({
                        top: container.scrollTop + childRect.top - containerRect.top - offset,
                        behavior: 'smooth'
                    });
                    
                    log('Scrolled to selected place');
                }
            }
        });
    });
}

function suggestChanges(hotel=false) {
    log('Suggesting changes');

    const payload = {
        'post_id': window.ironman.postId,
        'snonce': window.ironman.snonce,
        '_ajax_nonce': window.ironman.gnonce,
    }
    
    // Call the admin ajax function. Use callbacks to handle the response.
    callAdminAjax('suggestChanges', payload, 'suggestChanges', {
        onSuccess: (response) => {
            // Create new spinner
            updateLoadingSpinner('redirecting-to-revision', true, 'page-blocking', t('redirectingToRevisionSpinnerMessage'));
            if(hotel) {
                window.location.href = window.ironman.appConfig.editPostFormPage+"?revision_id="+response.data.new_revision_id+"&hotel_details=true";
            } else {
                window.location.href = window.ironman.appConfig.editPostFormPage+"?revision_id="+response.data.new_revision_id;
            }
            log('New revision created successfully.');
        },
        onFail: (error) => {
            log('Error:', error);
            alert(t('couldNotCreateNewRevision'));
        }
    }, 'page-blocking', t('creatingNewRevisionSpinnerMessage'));
}

function createNewListingClaim() {
    log('Creating new listing claim');

    const payload = {
        'post_id': window.ironman.postId,
        'snonce': window.ironman.snonce,
        '_ajax_nonce': window.ironman.gnonce,
    }
    
    // Call the admin ajax function. Use callbacks to handle the response.
    callAdminAjax('createNewListingClaim', payload, 'createNewListingClaim', {
        onSuccess: (response) => {
            // Create new spinner
            updateLoadingSpinner('redirecting-to-claim-edit-form', true, 'page-blocking', t('openingNewClaimEditFormSpinnerMessage'));
            window.location.href = window.ironman.appConfig.editPostFormPage+"?listing_claim_id="+response.data.new_listing_claim_id;
            log('New listing claim created successfully.');
        },
        onFail: (error) => {
            log('Error:', error);
            alert(t('couldNotCreateNewListingClaim'));
        }
    }, 'page-blocking', t('creatingNewListingClaimSpinnerMessage'));
}

function createNewPost(post_type, post_title, parent_post_id) {
    log('Creating new post');

    // Validate post type
    const validPostTypes = ['listing','hotel-room'];
    if(!validPostTypes.includes(post_type)) {
        log('Invalid post type:', post_type);
        return false;
    }
    
    post_title = sanitizeWpTitle(post_title);
    if(!post_title || post_title.length < 3) {
        log('Invalid post title');
        return false;
    }

    const payload = {
        post_id: window.ironman.postId,
        snonce: window.ironman.snonce,
        _ajax_nonce: window.ironman.gnonce,
        post_type: post_type,
        post_title: post_title,
        parent_post_id: parent_post_id,
    }
    
    callAdminAjax('suggestNewPost', payload, 'suggestNewPost', {
        onSuccess: (response) => {
            log('New post created successfully.');
            // Redirect to edit form of the new post
            if(post_type === 'listing') {
                // Create new spinner item
                updateLoadingSpinner('redirecting-to-revision', true, 'page-blocking', t('openingNewPostSpinnerMessage'));
                
                window.location.href = window.ironman.appConfig.editPostFormPage+"?listing_id="+response.data.new_post_id;
            } else if(post_type === 'hotel-room') {
                // Create new spinner item
                updateLoadingSpinner('redirecting-to-revision', true, 'page-blocking', t('openingNewPostSpinnerMessage'));

                window.location.href = window.ironman.appConfig.editPostFormPage+"?room_id="+response.data.new_post_id;
            }
        },
        onFail: (error) => {
            log('Error:', error);
            alert(t('couldNotCreateNewPost'));
        }
    }, 'page-blocking', t('creatingNewPostSpinnerMessage'));
}

//Handle translation
/**
 * Handle translation of the content.
 * @param {jQuery} $button - The button that was clicked.
 * @param {string} target_language - The target language to translate to.
 * @returns {boolean} - Returns false to prevent the default action of the button.
 */
function handleTranslation(target_language) {
    log('Handling translation for language:', target_language);

    // Validate target language
    // if(!hasKey(window.ironman.appConfig.postContentTranslationLanguages, target_language)) {
    //     log('Invalid target language:', target_language);
    //     return false;
    // }

    const $translationContainer = $('[data-label="post-content"]');
    
    //Start changing content
    //check if target language translation already exists.
    if(hasKey(window.ironman.postContentTranslations, target_language) && getValue(window.ironman.postContentTranslations, target_language).length > 0) {
        // Swap content
        $translationContainer.html(textToParagraphs(getValue(window.ironman.postContentTranslations, target_language)));
        log('Content swapped successfully.');
        updateLangSwitcherUI(target_language);
    } else {
        // Fetch new Google translation via server request.
        const data = {
            'post_id': window.ironman.postId,
            'target_language': target_language,
            'snonce': window.ironman.snonce
        }
        // Call the admin ajax function. Use callbacks to handle the response.
        callAdminAjax('getTranslatedContent', data, 'getTranslatedContent', {
            onSuccess: (response) => {
                if(response.success) {
                    log('Translation fetched successfully.');
                    // Add to postContentTranslations
                    window.ironman.postContentTranslations[target_language] = response.data.translated_content;

                    // Swap content
                    $translationContainer.html(textToParagraphs(response.data.translated_content));
                    log('Content swapped successfully.');
                    updateLangSwitcherUI(target_language);
                } else {
                    log('Error getting translation:', response.data.message);
                    return false;
                }
            },
            onFail: (error) => {
                log('Error getting translation:', error);
                return false;
            }
        }, 'non-blocking', t('gettingTranslationSpinnerMessage'));
    }
}

function updateLangSwitcherUI(target_language = 'original') {
    const $langSwitcher = $('[data-label="post-content-lang-switcher"]');
    const $langSwitcherMenu = $('[data-label="post-content-lang-switcher-menu"]');
    if(!$langSwitcher.length || !$langSwitcherMenu.length) {
        log('Lang switcher or menu not found');
        return false;
    }

    // Update UI
    // Update the lang switcher button
    $langSwitcher.find('[data-label="post-content-lang-switcher-button"]').html(getValue(window.ironman.appConfig.postContentTranslationLanguages, target_language).flag);

    //Unmark all languages as active
    $langSwitcherMenu.find('li a.active').each(function() {
        $(this).removeClass('active');
    });

    //Mark new language as active
    $langSwitcherMenu.find('li a[data-lang="'+target_language+'"]').addClass('active');
}

function hotelRender(hotels, group, meta = {}) {
    log('Rendering hotels', hotels);
    log('FILTER STATE', group, meta);
    
    const $container = $('[data-label="hotelListContainer"]');

    // 1. Empty the container.
    $container.empty();

    // 2. Get the templates
    const $template = getTemplate('singlePlace_hotelCardTemplate');
    const $swiperItemTemplate = getTemplate('singlePlace_hotelCardTemplate_swiperItemTemplate');
    const $serviceItemTemplate = getTemplate('singlePlace_hotelCardTemplate_serviceItemTemplate');
    const $amenityItemTemplate = getTemplate('singlePlace_hotelCardTemplate_amenityItemTemplate');
    const $roomTypeBadgeTemplate = getTemplate('singlePlace_hotelCardTemplate_roomTypeBadgeTemplate');
    if (!$template.length || !$swiperItemTemplate.length || !$serviceItemTemplate.length || !$amenityItemTemplate.length || !$roomTypeBadgeTemplate.length) {
        log('Template not found');
        return;
    }

    if (hotels.length > 0) {
        $('[data-label="hotelListNoItemsFound"]').addClass('d-none');
        $('[data-label="hotelListContainer"]').removeClass('d-none');
    } else {
        log('No hotels match your filters.');
        $('[data-label="hotelListNoItemsFound"]').removeClass('d-none');
        $('[data-label="hotelListContainer"]').addClass('d-none');
        return;
    }

    const $frag = $(document.createDocumentFragment());

    hotels.forEach(h => {
        log('ITEM:', h.title, 'services:', h.services);
        const $card = $template.clone(true, true);

        $card.find('[data-label="hotel-title"]').text(h.title).attr('href', h.link || '#');
        $card.find('[data-label="hotel-image"]').attr('src', h.featured_image_url || '').attr('alt', h.title || '');
        $card.find('[data-label="hotel-rating"]').text(h.rating_average || 0);
        $card.find('[data-label="hotel-rating-count"]').text('(' + (h.rating_count || 0) + ')');
        $card.find('[data-label="hotel-card-images-overlay"]').attr('href', h.link || '#');

        // Handle post save toggle button.
        const $savePostToggleButton = $card.find('[data-label="savePostToggleButton"]');
        $savePostToggleButton.attr('data-post-id', h.id);
        if(window.ironman.userData?.userSavedPosts && window.ironman.userData?.userSavedPosts.includes(h.id)) {
            $savePostToggleButton.children('i').removeClass().addClass('ci-heart-fill color-red');
        }
        if($savePostToggleButton.parent().data('bs-toggle') === 'tooltip') {
            const el      = $savePostToggleButton.parent()[0];
            const tooltip = bootstrap.Tooltip.getInstance(el);
            tooltip?.setContent({ '.tooltip-inner': 'Remove from favorites' });
        }
        
        if(h.booking_com_url && h.booking_com_url.length > 0) {
            $card.find('[data-label="hotel-booking-com-button"]').removeClass('d-none');
            $card.find('[data-label="hotel-booking-com-button-link"]').attr('href', h.booking_com_url || '#');
        }
        if(h.hotels_com_url && h.hotels_com_url.length > 0) {
            $card.find('[data-label="hotel-hotels-com-button"]').removeClass('d-none');
            $card.find('[data-label="hotel-hotels-com-button-link"]').attr('href', h.hotels_com_url || '#').removeClass('d-none');
        }

        // Hotel Card Images
        const $hotelCardImagesContainer = $card.find('[data-label="hotel-card-images-container"]');
        const hotelCardImagesArray = [];
        // Make sure media gallery is an array and has at least 1 item.
        if(Array.isArray(h.media_gallery) && h.media_gallery.length > 0) {
            h.media_gallery.forEach(media => {
                const $swiperItem = $swiperItemTemplate.clone(true, true);
                $swiperItem.find('[data-label="hotel-card-image"]').attr('src', media.media_url_thumbnail).attr('alt', media.caption || '');
                hotelCardImagesArray.push($swiperItem);
            });
        } else {
            // Create a dummy media item.
            const $swiperItem = $swiperItemTemplate.clone(true, true);
            hotelCardImagesArray.push($swiperItem);
        }
        $hotelCardImagesContainer.append(hotelCardImagesArray);

        // Services
        const $hotelSleepServicesContainer = $card.find('[data-label="hotel-sleep-services-container"]');
        const $hotelOtherServicesContainer = $card.find('[data-label="hotel-other-services-container"]');
        const hotelSleepServicesArray = [];
        const hotelOtherServicesArray = [];
        h.services.forEach(serviceSlug => {
            // Only show services that are in the sleep category.
            if(window.ironman.appConfig.servicesOffered.sleep.children[serviceSlug]) {
                const $serviceItem = $serviceItemTemplate.clone(true, true);
                $serviceItem.find('[data-label="hotel-service-icon"]').addClass(window.ironman.appConfig.servicesOffered.sleep.children[serviceSlug].icon || '');
                $serviceItem.find('[data-label="hotel-service-name"]').text(window.ironman.appConfig.servicesOffered.sleep.children[serviceSlug].name || '');
                hotelSleepServicesArray.push($serviceItem);
            } else {
                const $serviceItem = $serviceItemTemplate.clone(true, true);
                // Loop through the other services and find the one that matches the service slug.
                Object.keys(window.ironman.appConfig.servicesOffered).forEach(function(serviceCategorySlug) {
                    if(window.ironman.appConfig.servicesOffered[serviceCategorySlug].children[serviceSlug]) {
                        $serviceItem.find('[data-label="hotel-service-icon"]').addClass(window.ironman.appConfig.servicesOffered[serviceCategorySlug].children[serviceSlug].icon || '');
                        $serviceItem.find('[data-label="hotel-service-name"]').text(window.ironman.appConfig.servicesOffered[serviceCategorySlug].children[serviceSlug].name || '');
                        return;
                    }
                });
                hotelOtherServicesArray.push($serviceItem);
            }
        });
        if(hotelSleepServicesArray.length > 0) {
            $hotelSleepServicesContainer.append(hotelSleepServicesArray);
            $hotelSleepServicesContainer.removeClass('d-none');
        }
        if(hotelOtherServicesArray.length > 0) {
            $hotelOtherServicesContainer.append(hotelOtherServicesArray);
            $hotelOtherServicesContainer.removeClass('d-none');
        }

        // Rooms Types
        const $hotelRoomsTypesContainer = $card.find('[data-label="hotel-room-types-container"]');
        const $hotelRoomsTypesList = $hotelRoomsTypesContainer.find('[data-label="hotel-room-types-list"]');
        
        if(h.hotel_room_types && h.hotel_room_types.length > 0) {
            $hotelRoomsTypesContainer.removeClass('d-none');
            h.hotel_room_types.forEach(roomTypeSlug => {
                const $roomTypeBadge = $roomTypeBadgeTemplate.clone(true, true);
                $roomTypeBadge.find('[data-label="hotel-room-type-icon"]').addClass(window.ironman.appConfig.hotelRoomTypes[roomTypeSlug].icon || '');
                $roomTypeBadge.find('[data-label="hotel-room-type-name"]').text(window.ironman.appConfig.hotelRoomTypes[roomTypeSlug].name || '');
                $hotelRoomsTypesList.append($roomTypeBadge);
            });
        }

        // Operator
        const $hotelOperatorContainer = $card.find('[data-label="hotel-operator-badge"]');
        if(h.hotel_operator && h.hotel_operator === 'public' || h.hotel_operator === 'religious') {
            $hotelOperatorContainer.removeClass('d-none');
            $hotelOperatorContainer.find('[data-label="hotel-operator-icon"]').addClass(window.ironman.appConfig.hotelOperators[h.hotel_operator].icon || '');
            $hotelOperatorContainer.find('[data-label="hotel-operator-name"]').text(window.ironman.appConfig.hotelOperators[h.hotel_operator].name || '');
        }

        // Amenities
        const $hotelAmenitiesContainer = $card.find('[data-label="hotel-amenities-container"]');
        const hotelAmenitiesArray = [];
        h.hotel_amenities.forEach(amenitySlug => {
            const $amenityItem = $amenityItemTemplate.clone(true, true);
            $amenityItem.find('[data-label="hotel-amenity-icon"]').addClass(window.ironman.appConfig.hotelAmenities[amenitySlug].icon || '');
            $amenityItem.find('[data-label="hotel-amenity-name"]').text(window.ironman.appConfig.hotelAmenities[amenitySlug].name || '');
            hotelAmenitiesArray.push($amenityItem);
        });
        $hotelAmenitiesContainer.append(hotelAmenitiesArray);

        $frag.append($card);
    });

    $container.append($frag);

    // Init all the swipers in the container.
    initSwipersIn($container.get(0));

    // Pagination UI
    const $pag = $('[data-label="hotelListPagination"]');
    if ($pag.length && meta.totalPages && meta.totalPages > 1) {
        $pag.show();
        $pag.find('[data-label="hotelListPageInfo"]').text(`Page ${meta.page} of ${meta.totalPages}`);
        $pag.find('[data-label="hotelListPagePrev"]').prop('disabled', meta.page <= 1);
        $pag.find('[data-label="hotelListPageNext"]').prop('disabled', meta.page >= meta.totalPages);
    } else if ($pag.length) {
        $pag.hide();
    }

}

function listingRender(listings, group, meta = {}) {
    log('Rendering listings', listings, group, meta);
    log('FILTER STATE', group, meta);
    
    const $container = $('[data-label="servicesSwiperContainer"]');

    // 1. Empty the container.
    $container.empty();

    // 2. Get the template
    const $template = getTemplate('singlePlace_servicesSwiperItemTemplate');
    if (!$template.length) {
        log('Template not found');
        return;
    }

    if (listings.length > 0) {
        $('[data-label="servicesSwiperNoItemsFound"]').addClass('d-none');
        $('[data-label="servicesSwiperContainerWrapper"]').removeClass('d-none');
    } else {
        log('No listings match your filters.');
        $('[data-label="servicesSwiperNoItemsFound"]').removeClass('d-none');
        $('[data-label="servicesSwiperContainerWrapper"]').addClass('d-none');
        return;
    }

    const listingItems = [];

    // 3. Clone the template for each listing and append it to the container.
    listings.forEach(listing => {
        log('ITEM:', listing.title, listing);
        const $card = $template.clone(true, true);

        $card.find('[data-label="listing-title"]').text(listing.title).attr('href', listing.link || '#');
        $card.find('[data-label="listing-link"]').attr('href', listing.link || '#');
        $card.find('[data-label="listing-rating"]').text(listing.rating_average || 0);
        $card.find('[data-label="listing-rating-count"]').text('(' + (listing.rating_count || 0) + ')');

        // Media Gallery
        if(listing.media_gallery && listing.media_gallery.length > 0) {
            // Use first media item as the featured image.
            const featuredImageUrl = listing.media_gallery[0].media_url_thumbnail;
            $card.find('[data-label="listing-image"]').attr('src', featuredImageUrl).attr('alt', listing.title || '');
        }

        // Short Description
        $card.find('[data-label="listing-short-description"]').text(listing.short_desc || '');

        // Services
        const $listingServicesContainer = $card.find('[data-label="listing-services-container"]');
        const $serviceItemTemplate = getTemplate('singlePlace_servicesSwiperItem_singleServiceItemTemplate');
        const listingServicesArray = [];
        const filteredServices = filterMasterServicesList(listing.services || []);
        Object.entries(filteredServices).forEach(function([serviceCatSlug, serviceCat]) {
            Object.entries(serviceCat.children).forEach(function([serviceSlug, service]) {
                const $serviceItem = $serviceItemTemplate.clone(true, true);
                $serviceItem.find('[data-label="listing-service-icon"]').addClass(service.icon || '');
                $serviceItem.find('[data-label="listing-service-name"]').text(service.name || '');
                listingServicesArray.push($serviceItem);
            });
        });

        // Favorites icon
        const $savePostToggleButton = $card.find('[data-label="savePostToggleButton"]');
        $savePostToggleButton.attr('data-post-id', listing.id);
        if(window.ironman.userData?.userSavedPosts && window.ironman.userData?.userSavedPosts.includes(listing.id)) {
            $savePostToggleButton.find('[data-label="savePostToggleIcon"]').removeClass().addClass('ci-heart-fill color-red');
        }
        if($savePostToggleButton.parent().data('bs-toggle') === 'tooltip') {
            const el      = $savePostToggleButton.parent()[0];
            const tooltip = bootstrap.Tooltip.getInstance(el);
            tooltip?.setContent({ '.tooltip-inner': t('removeFromFavoritesTooltipMessage') });
        }

        // Append the service items to the listing services container.
        $listingServicesContainer.append(listingServicesArray);


        // Add $card to the listingItems array.
        listingItems.push($card);
    });

    $container.append(listingItems);

    // Update or Initialize the Swiper instance. (Swiper function is global).
    const servicesSwiperInstance = getSinglePlace_servicesSwiper_swiperInstance();
    if(servicesSwiperInstance) {
        log('Services swiper instance found, updating');
        servicesSwiperInstance.update();
    } else {
        log('Services swiper instance not found, initializing');
        initSwipersIn($container.get(0)); // Initialize the Swiper instance.
    }
}

async function showNewPostModal(postType, parentPostId, inputPlaceholder="Roncesvalles Hotel") {
    log('showNewPostModal', postType, parentPostId, inputPlaceholder);

    let postTitle = t('addNewListingModal_title');
    let inputLabel = t('addNewListingModal_inputLabel');

    if(postType === 'listing') {
        // Default values
    } else if(postType === 'hotel-room') {
        // Custom values
        postTitle = t('addNewRoomModal_title');
        inputLabel = t('addNewRoomModal_inputLabel');
    } else {
        log('Invalid post type:', postType);
        return false;
    }


    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = postTitle;
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'sm';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'newPost'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.
    
    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').attr('data-action', 'newPostModal_submit').attr('data-post-type', postType).attr('data-parent-post-id', parentPostId).text(t('create'));

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.

    let modalBody = getTemplate('createNewPostModalTemplate');

    // Start customizing the modal body...
    modalBody.find('[data-label="name-input-field"]').attr('placeholder', inputPlaceholder);
    modalBody.find('[data-label="name-input-label"]').text(inputLabel);
    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}

async function openRoomDetailsPopup(roomId) {
    log('openRoomDetailsPopup', roomId);

    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = t('roomDetailsModal_title');
    const modalFooter = null; // Use the default footer template and customize it.
    const modalDesktopSize = 'sm';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'roomDetails'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.

    let modalBody = getTemplate('roomDetailsModalTemplate');

    // Get the hotel room data from the global js variable.
    const hotelRoom = window.ironman.hotelRooms.find(room => room.ID === parseInt(roomId));
    if(hotelRoom) {
        // Start customizing the modal body...
        modalBody.find('[data-label="room-details-title"]').text(hotelRoom.post_title);




        // End customizing the modal body...
    } else {
        log('Hotel room not found');
        modalBody.empty().append('<div class="alert alert-warning">' + t('hotelRoomNotFound') + '</div>');
    }

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}

// Opens a modal with the write review form.
// Special case because it needs to use a div instead of a template so that the form can be instantiated on page load.
export async function openWriteReviewModal() {
    
    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = t('writeReviewModal_title');
    const modalFooter = null; // Use the default footer template and customize it.
    // const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'md';
    const modalMobileHeight = 'medium';
    const modalDataPurpose = 'writeReviewModal'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.

    // Customize the default modal footer...
    // modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').attr('data-action', 'writeReviewModal_submit').text('Submit Review');
    
    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    
    let modalBody = $('#writeReviewFormContent');
    // Set GLOBAL purpose variable.
    setFenixModalPurpose('writeReviewModal');

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}

async function showClaimListingModal(parentPostId) {
    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = t('claimListingModal_title');
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'sm';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'claimListing'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.
    
    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').attr('data-action', 'claimListingModal_submit').attr('data-parent-post-id', parentPostId).text(t('claimListing'));

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.

    let modalBody = getTemplate('claimListingModalTemplate');

    // Start customizing the modal body...

    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}